import { db, FieldValue, Timestamp } from "@/utils/Firebase";
import moment from 'moment'
import store from '@/store';
import { uniqueDayRoutine } from '@/utils/Exercises';

const STUDENT_WEEK_START_DAY = 1

const studentWeekStart = (weeksAgo = 0) => {
    const m = moment()
    const diff = (m.day() - STUDENT_WEEK_START_DAY + 7) % 7
    return m.subtract(diff, 'days').startOf('day').subtract(weeksAgo, 'weeks')
}

const fetchAssignedRoutinesRange = async (studentIds, startFull, endFull) => {
    const ids = (studentIds || []).filter(Boolean)
    if (ids.length === 0) return {}

    const chunks = []
    for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10))

    const snapshots = await Promise.all(
        chunks.map(chunk => db.collection('assignedRoutines')
            .where('studentId', 'in', chunk)
            .where('rawDate.full', '>=', startFull)
            .where('rawDate.full', '<=', endFull)
            .get())
    )

    const allRoutines = uniqueDayRoutine(
        snapshots.flatMap(snap => snap.docs.map(doc => ({ id: doc.id, ...doc.data() })))
    )

    const byStudent = {}
    ids.forEach(id => { byStudent[id] = [] })
    allRoutines.forEach(routine => {
        if (byStudent[routine.studentId]) byStudent[routine.studentId].push(routine)
    })
    return byStudent
}

const hasStartedRoutine = (routine) => {
    return (routine.blocks || []).some(block => block.completed
        || (block.exercises || []).some(exercise => exercise.completed))
}

const getDayStatus = (routine, currentDate, today) => {
    if (!routine) return 'none'
    if (routine.type === 'competence') return 'competence'
    if (routine.advance?.post) return 'done'
    if (!currentDate.isAfter(today)) return hasStartedRoutine(routine) ? 'partial' : 'not_realize'
    return 'to_realize'
}

const getCompetenceResult = (routine, currentDate, today) => {
    if (!routine || routine.type !== 'competence') return null
    if (routine.advance?.post === true) return 'done'
    return currentDate.isBefore(today, 'day') ? 'missed' : null
}

const getRoutineSummary = (routine) => {
    if (!routine) return null
    if (routine.type === 'competence') {
        const post = routine.advance?.postInfo || {}
        return {
            isCompetence: true,
            minutes: routine.minutes || null,
            duration: post.duration || null,
            effort: post.effort || null,
            position: post.position || null,
            team: post.team || null,
        }
    }
    const blocks = routine.blocks || []
    let minutes = 0
    let exercises = 0
    blocks.forEach(block => {
        (block.exercises || []).forEach(ex => {
            if (ex.type !== 'rest') exercises++
            minutes += ex?.config?.sets?.length || block.sets || 0
        })
    })
    const minutesAdded = Number(Number(minutes * 1.3).toFixed())
    const preInfo = routine.advance?.preInfo || null
    const effort = routine.advance?.postInfo?.effort
    const rpe = (effort === null || effort === undefined || effort === '' || isNaN(Number(effort)))
        ? null : Number(effort)
    return {
        isCompetence: false,
        blocks: blocks.length,
        exercises: exercises,
        minutes: minutes === minutesAdded ? `${minutes}` : `${minutes} - ${minutesAdded}`,
        wellness: preInfo ? calculateWellnessPreInfo(preInfo, 1) : null,
        wellnessLevel: preInfo ? wellnessLevel(sumWellnessPreInfo(preInfo)) : null,
        rpe: routine.advance?.post === true ? rpe : null,
        rpeLevel: routine.advance?.post === true && rpe !== null ? rpeLevel(rpe) : null,
    }
}

const buildWeekFromRoutines = (routines, weekStartMoment = studentWeekStart()) => {
    const startDate = weekStartMoment.clone()
    const today = moment()
    const week = []

    for (let i = 0; i < 7; i++) {
        const currentDate = startDate.clone().add(i, 'days')
        const dateFull = currentDate.format('YYYY-MM-DD')
        const routinesDay = (routines || []).filter(r => r.rawDate?.full === dateFull)

        const routine1 = routinesDay.find(r => (r.routineNumber || 1) === 1) || null
        const routine2 = routinesDay.find(r => r.routineNumber === 2) || null

        week.push({
            date: currentDate.format('DD-MM-YYYY'),
            dateFull,
            status: getDayStatus(routine1, currentDate, today),
            competenceResult: getCompetenceResult(routine1, currentDate, today),
            routine1: getRoutineSummary(routine1),
            routine2: getRoutineSummary(routine2),
        })
    }

    return week
}

const emptyWeek = (weekStartMoment = studentWeekStart()) => {
    const startDate = weekStartMoment.clone()
    const week = []
    for (let i = 0; i < 7; i++) {
        week.push({
            date: startDate.clone().add(i, 'days').format('DD-MM-YYYY'),
            dateFull: startDate.clone().add(i, 'days').format('YYYY-MM-DD'),
            status: 'none',
            competenceResult: null,
            routine1: null,
            routine2: null,
        })
    }
    return week
}

const wellnessDefaultColors = {
    veryBad: '🟤',
    bad: '🔴',
    somewhatBad: '🟠',
    normal: '⚪',
    somewhatGood: '🟡',
    good: '🟢',
    veryGood: '🔵',
}

const wellnessHexColors = {
    veryBad: '#8c1807',
    bad: '#ff2e01',
    somewhatBad: '#fca600',
    normal: '#919191',
    somewhatGood: '#5fa318',
    good: '#085c24',
    veryGood: '#1e3c6e',
}

const wellnessColors = {
    tired: '#66CCCC',
    energy: '#009966',
    stressed: '#3A5A6B',
    muscles_hurt: '#339999',
    sleep_night: '#1E3A4A',
}

const wellnessDimensions = [
    { key: 'tired', labelKey: 'statistics.tired', color: wellnessColors.tired },
    { key: 'energy', labelKey: 'statistics.energy', color: wellnessColors.energy },
    { key: 'stressed', labelKey: 'statistics.stressed', color: wellnessColors.stressed },
    { key: 'muscles_hurt', labelKey: 'statistics.muscles_hurt', color: wellnessColors.muscles_hurt },
    { key: 'sleep_night', labelKey: 'statistics.sleep_night', color: wellnessColors.sleep_night },
]


const CHANGE_GROUP_ORDER = ['sets', 'exercise', 'skip', 'pain', 'comment', 'video']

const setsValues = (config) => {
    if (!config) return []
    if (Array.isArray(config.sets) && config.sets.length) {
        return config.sets.map(set => {
            const values = {}
            ;(set?.data || []).forEach(item => {
                if (!item?.short_name || item.value == null || item.value === '') return
                values[item.short_name] = item.value
            })
            return values
        })
    }
    if (config.type === 'reps' && config.value != null) return [{ short_reps: config.value }]
    return []
}

const buildSetsChange = ({ block, exercise, exerciseId = null, exerciseIndex = null, previousConfig, config, t }) => {
    const before = setsValues(previousConfig)
    const after = setsValues(config)
    if (!after.length) return null

    const label = (name, value) => `${value}${t(`exercises.${name}`)}`

    const sets = after.map((values, index) => {
        const previous = before[index] || {}
        const set = {}
        Object.keys(values).forEach(name => {
            const from = previous[name]
            set[name] = {
                from: from == null || from === '' ? null : label(name, from),
                to: label(name, values[name]),
            }
        })
        return set
    })

    const changed = before.length !== after.length
        || sets.some(set => Object.values(set).some(value => value.from !== value.to))
    if (!changed) return null

    const legacy = (name) => after
        .map(values => (values[name] == null ? null : label(name, values[name])))
        .filter(Boolean)
        .join(' / ')

    return {
        type: 'setsChanged',
        block,
        exercise,
        exerciseId,
        exerciseIndex,
        sets,
        config: { reps: legacy('short_reps'), weight: legacy('short_weight') },
    }
}

const splitLegacySets = (value) => {
    return String(value || '')
        .split('/')
        .map(item => item.trim())
        .filter(Boolean)
        .map((item, index) => ({ label: `S${index + 1}`, from: null, to: item }))
}

const buildSetsGroups = (change) => {
    const groups = []

    if (Array.isArray(change.sets) && change.sets.length) {
        const names = []
        change.sets.forEach(set => Object.keys(set || {}).forEach(name => {
            if (!names.includes(name)) names.push(name)
        }))
        names.forEach(name => {
            const sets = change.sets
                .map((set, index) => ({
                    label: `S${index + 1}`,
                    from: set?.[name]?.from ?? null,
                    to: set?.[name]?.to ?? null,
                }))
                .filter(set => set.to != null && set.to !== '')
            if (sets.length) groups.push({ type: 'sets', name, sets })
        })
        return groups
    }

    ;[['short_reps', change.config?.reps], ['short_weight', change.config?.weight]].forEach(([name, value]) => {
        const sets = splitLegacySets(value)
        if (sets.length) groups.push({ type: 'sets', name, sets })
    })
    return groups
}

const studentChangeKey = (block, exercise) => {
    const id = exercise?.objectID || exercise?.id || null
    return id ? `${block}|@${id}` : null
}

const getStudentChanges = (routine) => {
    const result = new Map()
    if (!routine) return result

    const today = moment().startOf('day')
    const entries = []

    const entryFor = (block, name, id = null) => {
        let entry = entries.find(item => item.block === block && ((id && item.ids.has(id)) || (name && item.names.has(name))))
        if (!entry) {
            entry = { block, names: new Set(), ids: new Set(), groups: [] }
            entries.push(entry)
        }
        if (name) entry.names.add(name)
        if (id) entry.ids.add(id)
        return entry
    }

    const groupKey = (group) => group.type === 'sets' ? `sets|${group.name}` : group.type

    const setGroup = (entry, group) => {
        if (!group) return
        const index = entry.groups.findIndex(item => groupKey(item) === groupKey(group))
        if (index >= 0) entry.groups.splice(index, 1, group)
        else entry.groups.push(group)
    }

    ;(routine.advance?.changes || []).forEach(change => {
        if (!change?.type) return
        const block = change.block

        if (change.type === 'setsChanged') {
            const entry = entryFor(block, change.exercise, change.exerciseId)
            buildSetsGroups(change).forEach(group => setGroup(entry, group))
            return
        }

        if (change.type === 'exercise') {
            const entry = entryFor(block, change.oldExercise, change.exerciseId)
            const previous = entry.groups.find(group => group.type === 'exercise')
            setGroup(entry, { type: 'exercise', from: previous?.from || change.oldExercise, to: change.newExercise })
            entry.names.add(change.newExercise)
            return
        }

        if (change.type === 'skip') {
            setGroup(entryFor(block, change.exercise, change.exerciseId), { type: 'skip', exercise: change.exercise })
            return
        }

        if (change.type === 'painScale') {
            setGroup(entryFor(block, change.exercise, change.exerciseId), { type: 'pain', value: change.scale })
            return
        }

        if (change.type === 'video') {
            const expired = !!change.deleteIn && moment(change.deleteIn, 'YYYY-MM-DD').isSameOrBefore(today)
            setGroup(entryFor(block, change.exercise, change.exerciseId), { type: 'video', url: change.url || null, expired })
        }
    })

    ;(routine.blocks || []).forEach((block, index) => {
        ;(block?.exercises || []).forEach(exercise => {
            if (!exercise?.commentaryFromStudent) return
            const entry = entryFor(index + 1, exercise.name, exercise.objectID || exercise.id)
            setGroup(entry, { type: 'comment', text: exercise.commentaryFromStudent })
        })
    })

    entries.forEach(entry => {
        entry.groups.sort((a, b) => CHANGE_GROUP_ORDER.indexOf(a.type) - CHANGE_GROUP_ORDER.indexOf(b.type))
        entry.names.forEach(name => result.set(`${entry.block}|${name}`, entry.groups))
        entry.ids.forEach(id => result.set(`${entry.block}|@${id}`, entry.groups))
    })

    return result
}

const pruneStudentChanges = (changes, blocks) => {
    const present = new Set()
    ;(blocks || []).forEach((block, index) => {
        ;(block?.exercises || []).forEach(exercise => {
            if (!exercise) return
            if (exercise.name) present.add(`${index + 1}|${exercise.name}`)
            const id = exercise.objectID || exercise.id
            if (id) present.add(`${index + 1}|@${id}`)
        })
    })

    return (changes || []).filter(change => {
        if (!change?.type) return false
        const name = change.type === 'exercise' ? change.oldExercise : change.exercise
        if (change.exerciseId && present.has(`${change.block}|@${change.exerciseId}`)) return true
        return !!name && present.has(`${change.block}|${name}`)
    })
}

const updateAssignedRoutine = async (assignedRoutineId, payload, collection = 'assignedRoutines') => {
    if (!assignedRoutineId) return false
    try {
        await db.collection(collection).doc(assignedRoutineId).update(payload)
        return true
    } catch (error) {
        if (error?.code !== 'not-found') throw error
        return false
    }
}

const pruneRoutineChanges = async (routineId, blocks, collection = 'assignedRoutines') => {
    if (!routineId) return
    const ref = db.collection(collection).doc(routineId)
    const snap = await ref.get()
    const changes = snap.data()?.advance?.changes || []
    if (!changes.length) return
    const pruned = pruneStudentChanges(changes, blocks)
    if (pruned.length === changes.length) return
    await ref.update({ 'advance.changes': pruned })
}

const getCurrentUser = () => {
    return store.state.currentUser || null;
}

const getAccount = () => {
    return store.state.account || null;
}

const updateWellness = async (sum, preInfo) => {
    let account = getAccount()
    let user = getCurrentUser()
    let lastWellness = [...(account?.lastWellness || [])];
    lastWellness.push({
        sum,
        date: moment().format('DD-MM-YYYY'),
        ...preInfo,
    })
    while (lastWellness.length > 14) lastWellness.shift();

    await db.collection('users').doc(user.uid).update({
        lastWellness: lastWellness,
        lastWellnessDates: FieldValue.delete(),
    })

    await store.dispatch('bindUserAccount', user);

    return lastWellness
}

const wellnessVariables = ['sleep_night', 'muscles_hurt', 'tired', 'stressed', 'energy']

const getWellnessScore = (wellness, index) => {
    return wellnessVariables.map(varName => {
        const subArray = []
        for (let j = index - 5; j <= index; j++) {
            subArray.push(Number(wellness[j]?.[varName] || 0))
        }
        const avg = subArray.reduce((a, b) => a + b) / subArray.length
        const desvest = calcDesvest(subArray)
        return (Number(wellness[index]?.[varName] || 0) - avg) / desvest
    })
}

const updateCounters = async (duration) => {
    let user = getCurrentUser()
    let account = getAccount()
    const today = moment().format('YYYY-MM-DD')
    const countedToday = account?.counter?.streakDate === today
    const streak = (account?.counter?.streak || 0) + (countedToday ? 0 : 1)
    const streakRecord = Math.max(account?.counter?.streakRecord || 0, streak)

    await db.collection('users').doc(user.uid).update({
        'counter.week': FieldValue.increment(1),
        'counter.month': FieldValue.increment(1),
        'counter.durationWeek': FieldValue.increment(duration),
        'counter.durationMonth': FieldValue.increment(duration),
        'counter.streak': streak,
        'counter.streakRecord': streakRecord,
        'counter.streakDate': today,
    })
    await store.dispatch('bindUserAccount', user);
}

const STREAK_LOOKBACK_WEEKS = 12

const computeStreak = (routines, today) => {
    let streak = 0
    for (const routine of routines) {
        if (routine.type === 'competence') continue
        if (routine.advance?.post) { streak++; continue }
        if ((routine.rawDate?.full || '') === today) continue
        break
    }
    return streak
}

const computeAndPersistStreak = async (studentId) => {
    const uid = studentId || getCurrentUser()?.uid
    if (!uid) return { streak: 0, record: 0 }

    const account = getAccount()
    const today = moment().format('YYYY-MM-DD')
    const start = moment().subtract(STREAK_LOOKBACK_WEEKS, 'weeks').format('YYYY-MM-DD')

    const byStudent = await fetchAssignedRoutinesRange([uid], start, today)
    const routines = (byStudent[uid] || [])
        .filter(r => (r.routineNumber || 1) === 1)
        .sort((a, b) => (b.rawDate?.full || '').localeCompare(a.rawDate?.full || ''))

    const streak = computeStreak(routines, today)
    const record = Math.max(account?.counter?.streakRecord || 0, streak)

    if (account?.counter?.streak !== streak || account?.counter?.streakRecord !== record) {
        await db.collection('users').doc(uid).update({
            'counter.streak': streak,
            'counter.streakRecord': record,
        })
        await store.dispatch('bindUserAccount', getCurrentUser())
    }

    return { streak, record }
}

const getWellnessIcon = (wellness) => {
    let wellnessArray = fillWellness(wellness)
    return wellnessArray[wellnessArray.length - 1]
}

const filterValidWellness = (wellness) => {
    return (wellness || []).filter(w => w && typeof w === 'object' && w.date)
}

const fillWellness = (wellness) => {
    const items = filterValidWellness(wellness)
    const result = []
    for (let i = 5; i < items.length; i++) {
        result.push(getColorWellness(getWellnessScore(items, i)))
    }
    let newArray = new Array(7).fill(wellnessDefaultColors.normal)
    newArray.splice(0, result.length, ...result)
    return newArray
}

const calcDesvest = (array) => {
    const avg = array.reduce((a, b) => a + b) / array.length
    const desvests = array.map(x => Math.pow(x - avg, 2))
    if (desvests.every(element => element === 0)) return 1
    const sumDesvests = desvests.reduce((a, b) => a + b)
    return Math.sqrt(sumDesvests / array.length).toFixed(2)
}

const getWellnessStatusKey = (scoreOrScores) => {
    const scores = Array.isArray(scoreOrScores) ? scoreOrScores : [scoreOrScores]
    if (scores.some(s => Number.isNaN(s))) return 'normal'

    const lessNeg2 = scores.filter(s => s <= -2).length
    const lessNeg1 = scores.filter(s => s <= -1).length
    const greaterPos2 = scores.filter(s => s >= 2).length
    const greaterPos1 = scores.filter(s => s >= 1).length

    if (lessNeg1 >= 3 || lessNeg2 >= 2) return 'veryBad'
    if (lessNeg1 >= 2 || lessNeg2 >= 1) return 'bad'
    if (lessNeg1 >= 1) return 'somewhatBad'
    if (greaterPos1 >= 3 || greaterPos2 >= 2) return 'veryGood'
    if (greaterPos1 >= 2 || greaterPos2 >= 1) return 'good'
    if (greaterPos1 >= 1) return 'somewhatGood'
    return 'normal'
}

const getColorWellness = (scoreOrScores, asColor = false) => {
    const palette = asColor ? wellnessHexColors : wellnessDefaultColors
    return palette[getWellnessStatusKey(scoreOrScores)]
};

const sumWellnessPreInfo = (preInfo) => {
    if (!preInfo) return 5
    return (preInfo.sleep_night || 1) + (preInfo.muscles_hurt || 1) + (preInfo.tired || 1) + (preInfo.stressed || 1) + (preInfo.energy || 1)
}

const RPE_LEVEL_COLORS = { low: '#009966', mid: '#F5C842', high: '#E85C5C' }

const WELLNESS_LEVEL_COLORS = { good: '#339999', neutral: '#F5C842', bad: '#E85C5C' }

const rpeLevel = (value) => value >= 8 ? 'high' : value > 5 ? 'mid' : 'low'

const rpeColor = (value) => RPE_LEVEL_COLORS[rpeLevel(value)]

const wellnessLevel = (value) => value >= 18 ? 'good' : value >= 12 ? 'neutral' : 'bad'

const wellColor = (value) => WELLNESS_LEVEL_COLORS[wellnessLevel(value)]

const wellnessQuestionsCount = 5

const calculateWellnessPreInfo = (preInfo, decimals = 0) => {
    return Number(sumWellnessPreInfo(preInfo) / wellnessQuestionsCount).toFixed(decimals).replace('.0','')
}

export {
    wellnessDefaultColors,
    wellnessHexColors,
    wellnessColors,
    wellnessDimensions,
    getStudentChanges,
    pruneStudentChanges,
    pruneRoutineChanges,
    updateAssignedRoutine,
    studentChangeKey,
    buildSetsChange,
    updateWellness,
    updateCounters,
    getWellnessIcon,
    getWellnessScore,
    getWellnessStatusKey,
    getColorWellness,
    calcDesvest,
    sumWellnessPreInfo,
    wellnessQuestionsCount,
    rpeLevel,
    rpeColor,
    wellnessLevel,
    wellColor,
    calculateWellnessPreInfo,
    filterValidWellness,
    studentWeekStart,
    fetchAssignedRoutinesRange,
    getDayStatus,
    getRoutineSummary,
    buildWeekFromRoutines,
    emptyWeek,
    computeStreak,
    computeAndPersistStreak,
}